import {
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import React, {useContext, useState} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, BoltIcon, InformationCircleIcon, LanguageCircleIcon, Rocket01Icon, Tag01Icon} from "@hugeicons/core-free-icons";
import {CreateOrganization} from "../../organizations/Organizations";
import {Context as AppContext} from "../../../context/AppContext";
import {green, grey, orange} from "@mui/material/colors";
import {SignInContext} from "../../../context/SignInContext";

// https://docs.cloud.google.com/text-to-speech/docs/gemini-tts
const TTS_MODELS = [
  {model: "gemini-3.1-flash-tts-preview", name: "Gemini"},
  {model: null, name: "Default Model (Free)"},
];

const GEMINI_VOICE_NAMES = [
  "Achernar", "Achird", "Algenib", "Algieba", "Alnilam", "Aoede", "Autonoe", "Callirrhoe",
  "Charon", "Despina", "Enceladus", "Erinome", "Fenrir", "Gacrux", "Iapetus", "Kore",
  "Laomedeia", "Leda", "Orus", "Pulcherrima", "Puck", "Rasalgethi", "Sadachbia",
  "Sadaltager", "Schedar", "Sulafat", "Umbriel", "Vindemiatrix", "Zephyr", "Zubenelgenubi",
];

const GEMINI_AUDIO_ENCODINGS = ["LINEAR16", "MP3", "OGG_OPUS", "ALAW", "MULAW", "PCM"];

const DEFAULT_VOICE_GEMINI = {
  voice: {modelName: TTS_MODELS[0].model, name: "", languageCode: ""},
  audioConfig: {audioEncoding: "LINEAR16", pitch: 0, speakingRate: 1},
  input: {prompt: ""},
};

const SectionHeader = ({icon, title, description}) => (
  <Stack direction={"row"} gap={1.5} alignItems={"flex-start"} sx={{mb: 2}}>
    <Box sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 34,
      height: 34,
      borderRadius: 2,
      flexShrink: 0,
      bgcolor: "rgba(132, 145, 247, 0.12)",
      color: "primary.main"
    }}>
      {icon}
    </Box>
    <Box>
      <Typography fontWeight={600} fontSize={"0.95rem"}>{title}</Typography>
      {description && <Typography variant={"body2"} color={"text.secondary"} sx={{mt: 0.25}}>{description}</Typography>}
    </Box>
  </Stack>
);

export const ProjectForm = ({project, setProject, languages, organizations, fetchData}) => {
  const [createOrganization, setCreateOrganization] = useState(false);
  const {state: {projects}} = useContext(AppContext);
  const currentUser = useContext(SignInContext);
  const isAdmin = currentUser?.currentUser?.roles?.some(
      role => role?.toLowerCase() === "admin"
  );

  const handleChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;
    updateProject(key, value);
  }

  const updateProject = (key, value) => {
    if (key) {
      setProject(prev => ({...prev, [key]: value}))
    }
  }

  const selectedTtsModel = project?.voiceGemini?.voice?.modelName || null;

  const handleTtsModelChange = (e) => {
    const model = e.target.value || null;
    if (!model) {
      updateProject("voiceGemini", null);
      return;
    }
    setProject(prev => ({
      ...prev,
      voiceGemini: {
        ...DEFAULT_VOICE_GEMINI,
        ...(prev?.voiceGemini || {}),
        voice: {...DEFAULT_VOICE_GEMINI.voice, ...(prev?.voiceGemini?.voice || {}), modelName: model},
      },
    }));
  };

  const updateVoiceGemini = (section, key, value) => {
    setProject(prev => ({
      ...prev,
      voiceGemini: {
        ...DEFAULT_VOICE_GEMINI,
        ...(prev?.voiceGemini || {}),
        [section]: {
          ...DEFAULT_VOICE_GEMINI[section],
          ...(prev?.voiceGemini?.[section] || {}),
          [key]: value,
        },
      },
    }));
  };

  const fieldSx = {
    borderRadius: 2,
    "&.MuiOutlinedInput-root": {borderRadius: 2},
    "& .MuiOutlinedInput-root": {borderRadius: 2},
    "& .MuiOutlinedInput-notchedOutline": {borderRadius: 2},
  };
  const selectLabelSx = {
    backgroundColor: "background.paper",
    px: 0.5,
    zIndex: 1,
  };

  return (
    <>
      {isAdmin && createOrganization && (
          <CreateOrganization
              open={createOrganization}
              handleClose={() => setCreateOrganization(false)}
              organizations={organizations}
              onSuccess={fetchData}
          />
      )}
      <Stack direction={"column"} divider={<Divider sx={{borderColor: grey[100]}}/>} sx={{px: {xs: 2, md: 3}, py: {xs: 2, md: 3}}}>

        {/* General */}
        <Box sx={{pb: {xs: 2, md: 3}}}>
          <SectionHeader
            icon={<HugeiconsIcon icon={InformationCircleIcon} size={18}/>}
            title={"General Information"}
            description={"The basics - what this project is and who owns it."}
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name={"name"}
                label={"Project Name"}
                value={project?.name}
                onChange={handleChange}
                fullWidth
                sx={fieldSx}
                InputLabelProps={{shrink: true}}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name={"description"}
                label={"Description"}
                value={project?.description}
                fullWidth
                sx={fieldSx}
                InputLabelProps={{shrink: true}}
                rows={3} multiline
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Language & Voice Configuration */}
        <Box sx={{py: {xs: 2, md: 3}}}>
          <SectionHeader
            icon={<HugeiconsIcon icon={LanguageCircleIcon} size={18}/>}
            title={"Language & Voice Configuration"}
            description={"Configure how the assistant understands and speaks to users."}
          />
          <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
            <Box sx={{flex: 1}}>
              <FormControl fullWidth>
                <InputLabel id={`language-label`} shrink={true} sx={{...selectLabelSx, textTransform: "capitalize"}}>Language</InputLabel>
                <Select
                  labelId={`language-label`}
                  id={`language-select`}
                  name={"language"}
                  label={"Language"}
                  value={project?.language || ""}
                  sx={fieldSx}
                  onChange={e => {
                    const lang = languages.find(i => i.id === e.target.value);
                    setProject(prev => ({...prev, "language": lang?.id || null, "lang": lang?.lang || null}))
                  }}
                >
                  <MenuItem value={""}><em>None</em></MenuItem>
                  {languages?.map(item => <MenuItem key={item?.id}
                                                    value={item?.id}>{item?.language} ({item?.code})</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{flex: 1}}>
              <FormControl fullWidth>
                <InputLabel id={`tts-model-label`} shrink={true} sx={selectLabelSx}>Voice Model</InputLabel>
                <Select
                  labelId={`tts-model-label`}
                  id={`tts-model-select`}
                  label={"Voice Model"}
                  value={selectedTtsModel || ""}
                  displayEmpty
                  renderValue={value => TTS_MODELS.find(item => (item.model || "") === value)?.name || "Default Model (Free)"}
                  sx={fieldSx}
                  onChange={handleTtsModelChange}
                >
                  {TTS_MODELS.map(item => (
                    <MenuItem key={item.model || "default"} value={item.model || ""}>{item.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>

          {selectedTtsModel &&
            <Grid container spacing={2} sx={{mt: 0}}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id={`voice-name-label`} shrink={true} sx={selectLabelSx}>Voice</InputLabel>
                  <Select
                    labelId={`voice-name-label`}
                    id={`voice-name-select`}
                    label={"Voice"}
                    value={project?.voiceGemini?.voice?.name || ""}
                    sx={fieldSx}
                    onChange={e => updateVoiceGemini("voice", "name", e.target.value)}
                  >
                    <MenuItem value={""}><em>None</em></MenuItem>
                    {GEMINI_VOICE_NAMES.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={"Voice Language Code"}
                  placeholder={"e.g. en-US"}
                  helperText={"BCP-47 code"}
                  value={project?.voiceGemini?.voice?.languageCode || ""}
                  onChange={e => updateVoiceGemini("voice", "languageCode", e.target.value)}
                  fullWidth
                  sx={fieldSx}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                    <InputLabel id={`audio-encoding-label`} shrink={true} sx={selectLabelSx}>Audio Encoding</InputLabel>
                  <Select
                    labelId={`audio-encoding-label`}
                    id={`audio-encoding-select`}
                    label={"Audio Encoding"}
                    value={project?.voiceGemini?.audioConfig?.audioEncoding || "LINEAR16"}
                    sx={fieldSx}
                    onChange={e => updateVoiceGemini("audioConfig", "audioEncoding", e.target.value)}
                  >
                    {GEMINI_AUDIO_ENCODINGS.map(enc => <MenuItem key={enc} value={enc}>{enc}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={"Pitch"}
                  type={"number"}
                  helperText={"-20.0 to 20.0"}
                  inputProps={{step: 0.1, min: -20, max: 20}}
                  value={project?.voiceGemini?.audioConfig?.pitch ?? 0}
                  onChange={e => updateVoiceGemini("audioConfig", "pitch", e.target.value === "" ? null : Number(e.target.value))}
                  fullWidth
                  sx={fieldSx}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={"Speaking Rate"}
                  type={"number"}
                  helperText={"0.25 to 4.0"}
                  inputProps={{step: 0.05, min: 0.25, max: 4}}
                  value={project?.voiceGemini?.audioConfig?.speakingRate ?? 1}
                  onChange={e => updateVoiceGemini("audioConfig", "speakingRate", e.target.value === "" ? null : Number(e.target.value))}
                  fullWidth
                  sx={fieldSx}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={"Style Prompt"}
                  placeholder={"e.g. Speak in a calm, friendly tone. Use [sigh] and [whispering] tags sparingly."}
                  helperText={"Optional natural-language delivery guidance, supports tags like [whispering], [laughing], [extremely fast]"}
                  value={project?.voiceGemini?.input?.prompt || ""}
                  onChange={e => updateVoiceGemini("input", "prompt", e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  sx={fieldSx}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
            </Grid>
          }
        </Box>

        {/* Deployment */}
        <Box sx={{py: {xs: 2, md: 3}}}>
          <SectionHeader
            icon={<HugeiconsIcon icon={Rocket01Icon} size={18}/>}
            title={"Deployment"}
            description={"Where changes to this project are published."}
          />
          <Stack direction={"column"} gap={2}>
            <ToggleButtonGroup
              exclusive
              value={project?.mode || "stage"}
              onChange={(e, value) => {
                if (!value) return;
                setProject(prev => ({
                  ...prev,
                  mode: value,
                  deploymentProject: value === "production" ? null : prev?.deploymentProject || null
                }))
              }}
              sx={{
                alignSelf: "flex-start",
                borderRadius: 2,
                p: 0.75,
                gap: 0.5,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  border: "none !important",
                  borderRadius: "999px !important",
                  px: 2.5,
                  py: 1,
                  fontWeight: 500,
                  color: "text.secondary"
                },
                "& .Mui-selected": {
                  bgcolor: "rgba(132, 145, 247, 0.3) !important",
                  color: "primary.main !important"
                }
              }}
            >
              <ToggleButton value={"stage"}>Stage</ToggleButton>
              <ToggleButton value={"production"}>Production</ToggleButton>
            </ToggleButtonGroup>
            {
              project?.mode === "stage" &&
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id={`deployment-label`} shrink={true} sx={{...selectLabelSx, textTransform: "capitalize"}}>Deployment Project</InputLabel>
                    <Select
                      variant={"outlined"}
                      labelId={`deployment-label`}
                      id={`deployment-select`}
                      name={"deploymentProject"}
                      label={"Deployment Project"}
                      value={project?.deploymentProject || ""}
                      sx={fieldSx}
                      onChange={e => {
                        const value = e.target.value;
                        setProject(prev => ({...prev, deploymentProject: value === "" ? null : value}))
                      }}
                    >
                      <MenuItem value={""}><em>None</em></MenuItem>
                      {projects?.filter(item => item?.id !== project?.id && item?.settings?.mode === "production")?.map(item =>
                        <MenuItem key={item?.id} value={item?.id}>{item?.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            }
          </Stack>
        </Box>

        {/* Classification */}
        <Box sx={{py: {xs: 2, md: 3}}}>
          <SectionHeader
            icon={<HugeiconsIcon icon={Tag01Icon} size={18}/>}
            title={"Classification"}
            description={"Helps organize this project across the workspace."}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack direction={"row"} gap={1} alignItems={"flex-start"}>
                <FormControl fullWidth>
                  <InputLabel id={`organization-label`} shrink={true} sx={{...selectLabelSx, textTransform: "capitalize"}}>Organization</InputLabel>
                  <Select
                    labelId={`organization-label`}
                    id={`organization-select`}
                    name={"organization"}
                    label={"Organization"}
                    value={project?.organization || ""}
                    sx={fieldSx}
                    onChange={e => {
                      const org = organizations.find(i => i?.id === e.target.value);
                      updateProject("organization", org?.id || null)
                    }}
                  >
                    <MenuItem value={""}><em>None</em></MenuItem>
                    {organizations?.map(item => <MenuItem key={item?.id} value={item?.id}>{item?.name}</MenuItem>)}
                  </Select>
                </FormControl>
                {isAdmin && (
                    <Tooltip title={"Create Organization"}>
                      <IconButton
                          onClick={() => setCreateOrganization(true)}
                          sx={{border: 1, borderColor: grey[200], borderRadius: 2, mt: "4px"}}
                      >
                        <HugeiconsIcon icon={Add01Icon} size={18}/>
                      </IconButton>
                    </Tooltip>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id={`status-label`} shrink={true} sx={selectLabelSx}>Status</InputLabel>
                <Select
                  variant={"outlined"}
                  labelId={`status-label`}
                  id={`status-select`}
                  name={"status"}
                  label={"Status"}
                  value={project?.status || ""}
                  sx={fieldSx}
                  onChange={e => {
                    const value = e.target.value;
                    setProject(prev => ({...prev, status: value === "" ? null : value}))
                  }}
                >
                  <MenuItem value={""}><em>None</em></MenuItem>
                  <MenuItem value={"BUILDING_SCENARIOS"}>Building Scenarios</MenuItem>
                  <MenuItem value={"IN_PROGRESS"}>In Progress</MenuItem>
                  <MenuItem value={"TESTING"}>Testing</MenuItem>
                  <MenuItem value={"PRODUCTION"}>Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ToggleButtonGroup
                exclusive
                value={project?.projectType || "VOICE_BOT"}
                onChange={(e, value) => {
                  if (!value) return;
                  updateProject("projectType", value);
                }}
                sx={{
                  alignSelf: "flex-start",
                  borderRadius: 2,
                  p: 0.75,
                  gap: 0.5,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    border: "none !important",
                    borderRadius: "999px !important",
                    px: 2.5,
                    py: 1,
                    fontWeight: 500,
                    color: "text.secondary"
                  },
                  "& .Mui-selected": {
                    bgcolor: "rgba(132, 145, 247, 0.3) !important",
                    color: "primary.main !important"
                  }
                }}
              >
                <ToggleButton value={"VOICE_BOT"}>Voice Assistant</ToggleButton>
                <ToggleButton value={"CHAT_BOT"}>Chatbot Assistant</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </Box>

        {/* Availability */}
        <Box sx={{pt: {xs: 2, md: 3}}}>
          <SectionHeader
            icon={<HugeiconsIcon icon={BoltIcon} size={18}/>}
            title={"Availability"}
          />
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={2}
            sx={{
              borderRadius: 3,
              p: 2,
              border: 1,
              borderColor: project?.is_live ? "rgba(9, 217, 42, 0.25)" : orange[100],
              bgcolor: project?.is_live ? "rgba(9, 217, 42, 0.06)" : orange[50]
            }}
          >
            <Stack direction={"row"} gap={1.5} alignItems={"center"}>
              <Box sx={{
                width: 10, height: 10, borderRadius: "50%",
                bgcolor: project?.is_live ? green[500] : orange[400],
                boxShadow: project?.is_live ? `0 0 0 4px rgba(9, 217, 42, 0.15)` : `0 0 0 4px ${orange[100]}`
              }}/>
              <Box>
                <Typography fontWeight={600} fontSize={"0.85rem"}>
                  {project?.is_live ? "Live" : "Maintenance Mode"}
                </Typography>
                <Typography variant={"body2"} color={"text.secondary"} sx={{maxWidth: "sm"}}>
                  {project?.is_live
                    ? "Users are being served by this project normally."
                    : "Calls are redirected to the configured fallback number."}
                </Typography>
              </Box>
            </Stack>
            <Switch
              checked={!!project?.is_live} size={"medium"}
              onChange={(e, checked) => updateProject("is_live", checked)}
              color="secondary"
            />
          </Stack>
        </Box>
      </Stack>
    </>
  )
}
